import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Search, Send, MessageCircle, X, Users as UsersIcon, Paperclip, Plus, FileText, Video, Phone, MoreVertical, Reply, Smile, Trash2, Hash, Archive, Radio } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCall } from "@/contexts/CallContext";
import { scrollWithinContainer } from "@/lib/scroll";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * Unified Chat page — WhatsApp / Slack-style.
 *
 * Supports 4 conversation kinds behind one UI:
 *   - `dm`   : 1:1 direct messages (backed by `direct_messages` collection)
 *   - `grp`  : custom groups the user creates (`chat_groups`)
 *   - `team` : broadcast to everyone in the caller's team_section
 *   - `unit` : broadcast to everyone in a specific org_unit
 *
 * Wire protocol:
 *   - Inbox list  → GET /chat/threads (+ GET /workspace/dm/inbox for DMs)
 *   - Messages    → GET /chat/threads/{kind}/{id}/messages
 *                 → GET /workspace/dm/with/{uid}
 *   - Send        → POST /chat/threads/{kind}/{id}/messages
 *                 → POST /workspace/dm/send
 *   - Realtime    → ws  /api/ws/chat/{thread_key} pushes {type,data}
 *
 * The bottom-right floating chat continues to use `/workspace/dm/*` so the
 * two surfaces stay in sync automatically.
 */

const KIND_ICON = {
  dm: MessageCircle, grp: UsersIcon, team: UsersIcon, unit: UsersIcon,
};

export default function ChatPage() {
  const { user } = useAuth();
  const { startCall } = useCall();
  const [dmInbox, setDmInbox] = useState([]);
  const [groupThreads, setGroupThreads] = useState([]);
  const [messagesByKey, setMessagesByKey] = useState({});
  const [selected, setSelected] = useState(null); // {kind, id, name, user_id?}
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // message object or null
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [sendAsIndividual, setSendAsIndividual] = useState(false); // group threads only
  const [archivedKeys, setArchivedKeys] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const userWsRef = useRef(null);

  const threadKey = selected ? (selected.kind === "dm" ? `dm:${selected.user_id}` : `${selected.kind}:${selected.id}`) : null;
  const wsThreadKey = selected ? (selected.kind === "dm" ? `dm:${[user?.user_id, selected.user_id].sort().join("_")}` : `${selected.kind}:${selected.id}`) : null;

  // ------- Inbox loaders -------
  const loadDmInbox = async () => {
    try { const r = await api.get("/workspace/dm/inbox"); setDmInbox(r.data || []); }
    catch { /* silent */ }
  };
  const loadGroupThreads = async () => {
    try { const r = await api.get("/chat/threads"); setGroupThreads(r.data || []); }
    catch { /* silent */ }
  };
  useEffect(() => {
    loadDmInbox(); loadGroupThreads();
    // Reduced polling — user-level WebSocket (below) is the primary path.
    // Poll is just a safety net for offline / connection-drop scenarios.
    const t = setInterval(() => { loadDmInbox(); loadGroupThreads(); }, 60_000);
    return () => clearInterval(t);
  }, []);

  // Per-user presence WebSocket — receives ANY inbound message notification
  // so the inbox refreshes live without polling and closed threads flag
  // themselves as unread instantly (WhatsApp-style).
  useEffect(() => {
    if (!user?.user_id) return;
    const token = localStorage.getItem("kb_session_token") || "";
    const wsBase = (BACKEND_URL || "").replace(/^http/, "ws");
    const url = `${wsBase}/api/ws/user/${encodeURIComponent(user.user_id)}?token=${encodeURIComponent(token)}`;
    let sock;
    try { sock = new WebSocket(url); } catch { return; }
    userWsRef.current = sock;
    sock.onmessage = () => { loadDmInbox(); loadGroupThreads(); window.dispatchEvent(new CustomEvent("chat:refresh")); };
    sock.onerror = () => { /* silent */ };
    return () => { try { sock.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Archived-thread preference (kept in a per-user Mongo doc via /chat/archive).
  const loadArchived = async () => {
    try { const r = await api.get("/chat/archived"); setArchivedKeys(r.data?.archived_thread_keys || []); }
    catch { /* silent */ }
  };
  useEffect(() => { loadArchived(); }, []);

  // ------- Selected thread messages -------
  const loadMessages = async (sel) => {
    if (!sel) return;
    try {
      const url = sel.kind === "dm" ? `/workspace/dm/with/${sel.user_id}` : `/chat/threads/${sel.kind}/${sel.id}/messages`;
      const r = await api.get(url);
      setMessagesByKey((prev) => ({ ...prev, [threadKey]: r.data || [] }));
    } catch { /* silent */ }
  };
  useEffect(() => { if (selected) loadMessages(selected); /* eslint-disable-next-line */ }, [selected?.kind, selected?.id, selected?.user_id]);

  // Whenever a thread is opened (or Chat page mounts) tell the sidebar badge
  // to re-fetch — GET /workspace/dm/with/{uid} flips read=True server-side.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("chat:refresh"));
  }, [selected?.user_id, selected?.id, selected?.kind]);

  // ------- WebSocket live updates -------
  useEffect(() => {
    if (!wsThreadKey) return;
    // Session token lives in localStorage (see lib/api.js). Build the ws URL
    // by swapping http(s)→ws(s) on REACT_APP_BACKEND_URL.
    const token = localStorage.getItem("kb_session_token") || "";
    const wsBase = (BACKEND_URL || "").replace(/^http/, "ws");
    const url = `${wsBase}/api/ws/chat/${encodeURIComponent(wsThreadKey)}?token=${encodeURIComponent(token)}`;
    let sock;
    try { sock = new WebSocket(url); } catch { return; }
    wsRef.current = sock;
    sock.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "chat_message" || msg.type === "dm_message") {
          setMessagesByKey((prev) => {
            const arr = prev[threadKey] || [];
            if (arr.some((m) => m.id === msg.data.id)) return prev;
            return { ...prev, [threadKey]: [...arr, msg.data] };
          });
        } else if (msg.type === "reaction") {
          setMessagesByKey((prev) => {
            const arr = (prev[threadKey] || []).map((m) => m.id === msg.data.message_id ? { ...m, reactions: msg.data.reactions } : m);
            return { ...prev, [threadKey]: arr };
          });
        } else if (msg.type === "delete") {
          setMessagesByKey((prev) => {
            const arr = (prev[threadKey] || []).map((m) => m.id === msg.data.message_id ? { ...m, deleted: true, body: "", attachments: [] } : m);
            return { ...prev, [threadKey]: arr };
          });
        }
      } catch { /* ignore */ }
    };
    sock.onerror = () => { /* silent */ };
    return () => { try { sock.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsThreadKey]);

  useEffect(() => { scrollWithinContainer(endRef.current); }, [messagesByKey, threadKey]);

  // Listen for "join call" clicks fired from any bubble.
  useEffect(() => {
    const onJoin = (e) => {
      const { url, audioOnly } = e.detail || {};
      if (url) startCall({ provider: "daily", roomUrl: url, displayName: user?.name || "Guest", audioOnly });
    };
    window.addEventListener("khojbyte:join-call", onJoin);
    return () => window.removeEventListener("khojbyte:join-call", onJoin);
  }, [startCall, user?.name]);

  // ------- User search (for starting a DM) -------
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/workspace/dm/search-users`, { params: { q } });
        setSearchResults(r.data || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  // ------- Attachment upload (via chat-specific endpoint — no Drive needed) -------
  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Max 12MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/chat/upload", fd);
      setAttachments((a) => [...a, { name: r.data.name, url: r.data.url, size: r.data.size, mimeType: r.data.mimeType }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    finally { setBusy(false); e.target.value = ""; }
  };

  // ------- Phone / video call (inline via CallContext) --------------------
  // Reuse the workspace Daily.co endpoint (idempotent by room name) — for
  // DMs we use a stable `dm_<sorted-user-ids>` room, for groups the group id
  // becomes the room id. The room mounts as a persistent inline panel so it
  // survives route changes just like workspace calls.
  const initiateCall = async (audioOnly) => {
    if (!selected) return;
    const roomId = selected.kind === "dm"
      ? `dm_${[user?.user_id, selected.user_id].sort().join("__")}`
      : `${selected.kind}_${selected.id}`;
    try {
      const r = await api.post(`/workspace/${roomId}/call/daily-room?audio_only=${audioOnly ? "true" : "false"}`);
      const roomUrl = r.data?.url;
      if (!roomUrl) throw new Error("No room URL");
      startCall({ provider: "daily", roomUrl, displayName: user?.name || "Guest", audioOnly });
      // Post an invite in the thread so the other participant sees the join link.
      const inviteBody = `📞 ${audioOnly ? "Voice" : "Video"} call started — join: ${roomUrl}`;
      try {
        if (selected.kind === "dm") {
          await api.post("/workspace/dm/send", { to_user_id: selected.user_id, body: inviteBody, attachments: [] });
        } else {
          await api.post(`/chat/threads/${selected.kind}/${selected.id}/messages`, { body: inviteBody, attachments: [] });
        }
        loadMessages(selected);
      } catch { /* silent — panel is up regardless */ }
    } catch { toast.error("Couldn't start call"); }
  };

  // ------- Reactions / delete ---------------------------------------------
  const toggleReaction = async (m, emoji) => {
    try {
      const r = await api.post(`/chat/message/${m.id}/react`, { emoji, kind: selected.kind });
      setMessagesByKey((prev) => {
        const arr = (prev[threadKey] || []).map((x) => x.id === m.id ? { ...x, reactions: r.data.reactions } : x);
        return { ...prev, [threadKey]: arr };
      });
    } catch { /* silent */ }
  };
  const deleteMessage = async (m) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await api.delete(`/chat/message/${m.id}?kind=${selected.kind}`);
      setMessagesByKey((prev) => {
        const arr = (prev[threadKey] || []).map((x) => x.id === m.id ? { ...x, deleted: true, body: "", attachments: [] } : x);
        return { ...prev, [threadKey]: arr };
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ------- Send -------
  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body && attachments.length === 0) return;
    if (!selected) return;
    // Groups + "send as individual" fan out one DM per member (WhatsApp
    // Broadcast list style). We resolve the member list on the server via
    // /chat/broadcast-roles when the group is a role broadcast, otherwise
    // via /chat/groups member list on the client.
    if (sendAsIndividual && selected.kind !== "dm") {
      setBusy(true);
      try {
        // Collect member ids for the current group/team/unit.
        let memberIds = [];
        if (selected.kind === "grp") {
          const grps = await api.get("/chat/groups");
          const g = (grps.data || []).find((x) => x.id === selected.id);
          memberIds = (g?.member_user_ids || []).filter((uid) => uid !== user?.user_id);
        } else if (selected.kind === "unit") {
          const u = await api.get("/org/units");
          const unit = (u.data || []).find((x) => x.id === selected.id);
          memberIds = ((unit?.members || []).map((m) => m.user_id) || []).filter((uid) => uid !== user?.user_id);
        } else if (selected.kind === "team") {
          const r = await api.get(`/workspace/dm/search-users`, { params: { q: "" } });
          memberIds = ((r.data || []).filter((x) => x.team_section === selected.id).map((x) => x.user_id)).filter((uid) => uid !== user?.user_id);
        }
        if (memberIds.length === 0) { toast.error("No individual recipients resolved"); setBusy(false); return; }
        for (const uid of memberIds) {
          await api.post("/workspace/dm/send", { to_user_id: uid, body, attachments, reply_to_id: replyTo?.id || null });
        }
        toast.success(`Sent to ${memberIds.length} member(s) individually`);
        setDraft(""); setAttachments([]); setReplyTo(null); setSendAsIndividual(false);
        loadDmInbox();
        window.dispatchEvent(new CustomEvent("dm:refresh"));
      } catch { toast.error("Broadcast send failed"); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      let saved;
      if (selected.kind === "dm") {
        const r = await api.post("/workspace/dm/send", { to_user_id: selected.user_id, body, attachments, reply_to_id: replyTo?.id || null });
        saved = r.data;
      } else {
        const r = await api.post(`/chat/threads/${selected.kind}/${selected.id}/messages`, { body, attachments, reply_to_id: replyTo?.id || null });
        saved = r.data;
      }
      setMessagesByKey((prev) => ({ ...prev, [threadKey]: [...(prev[threadKey] || []), saved] }));
      setDraft(""); setAttachments([]); setReplyTo(null);
      loadDmInbox(); loadGroupThreads();
      window.dispatchEvent(new CustomEvent("dm:refresh"));
    } catch { toast.error("Send failed"); }
    finally { setBusy(false); }
  };

  const archiveThread = async (sel, archived = true) => {
    if (!sel) return;
    try {
      const ident = sel.kind === "dm" ? sel.user_id : sel.id;
      await api.post("/chat/archive", { kind: sel.kind, ident, archived });
      toast.success(archived ? "Conversation archived" : "Restored");
      if (archived) setSelected(null);
      loadArchived();
    } catch { toast.error("Archive failed"); }
  };

  const deleteThread = async (sel) => {
    if (!sel) return;
    if (!window.confirm("Delete this entire conversation? This can't be undone.")) return;
    try {
      const ident = sel.kind === "dm" ? sel.user_id : sel.id;
      await api.post("/chat/thread/delete", { kind: sel.kind, ident });
      toast.success("Conversation deleted");
      setSelected(null);
      loadDmInbox(); loadGroupThreads(); loadArchived();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ------- Combined left rail -------
  const combined = useMemo(() => {
    const rows = [];
    if (searchResults.length > 0) {
      searchResults.forEach((u) => rows.push({
        thread_key: `search_${u.user_id}`,
        select: { kind: "dm", user_id: u.user_id, name: u.name || u.email },
        title: u.name || u.email, subtitle: u.role || "user",
        unread: 0, kind: "dm", _search: true,
      }));
      return rows;
    }
    dmInbox.forEach((row) => rows.push({
      thread_key: row.thread_key,
      select: { kind: "dm", user_id: row.other_user.user_id, name: row.other_user.name },
      title: row.other_user.name || row.other_user.email,
      subtitle: (row.last_from_me ? "You: " : "") + (row.last_body || "…"),
      unread: row.unread, kind: "dm", at: row.last_at,
    }));
    groupThreads.forEach((row) => {
      const [kind, id] = row.thread_key.split(":");
      rows.push({
        thread_key: row.thread_key,
        select: { kind, id, name: row.name },
        title: row.name, subtitle: row.last_body || "…",
        unread: 0, kind, at: row.last_at,
      });
    });
    rows.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    const archivedSet = new Set(archivedKeys);
    return rows.filter((r) => showArchived ? archivedSet.has(r.thread_key) : !archivedSet.has(r.thread_key));
  }, [dmInbox, groupThreads, searchResults, archivedKeys, showArchived]);

  const messages = messagesByKey[threadKey] || [];

  return (
    <div data-testid="chat-page" className="h-[calc(100vh-6rem)] flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MESSAGES</div>
          <h1 className="font-serif text-2xl lg:text-3xl text-kb-ink mt-1 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-kb-primary" strokeWidth={1.5} /> Chat
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowNewChat(true)} data-testid="chat-new-conversation">
            <Plus className="w-3.5 h-3.5" /> New chat
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBroadcast(true)} data-testid="chat-broadcast-roles">
            <Radio className="w-3.5 h-3.5" /> Broadcast
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowGroupModal(true)} data-testid="chat-new-group">
            <UsersIcon className="w-3.5 h-3.5" /> New group
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex border border-slate-200 rounded-sm bg-white overflow-hidden">
        {/* LEFT RAIL */}
        <aside className="w-72 shrink-0 border-r border-slate-200 flex flex-col bg-slate-50">
          <div className="p-2.5 border-b border-slate-200">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-kb-ink-muted" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Find a person or channel…"
                className="kb-input text-sm pl-8 w-full" data-testid="chat-search" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-kb-ink-muted hover:text-kb-primary">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className={`mt-2 w-full kb-mono text-[10px] uppercase tracking-wider py-1 rounded-sm inline-flex items-center justify-center gap-1.5 border ${showArchived ? "bg-kb-ink text-white border-kb-ink" : "border-slate-200 text-kb-ink-muted hover:border-kb-ink"}`}
              data-testid="chat-toggle-archived"
            >
              <Archive className="w-3 h-3" /> {showArchived ? `Archived (${archivedKeys.length}) · Back to inbox` : `Show archived (${archivedKeys.length})`}
            </button>
          </div>
          <ScrollArea className="flex-1">
            {combined.length === 0 ? (
              <div className="p-4 text-center text-xs text-kb-ink-muted">No conversations yet.<br/>Search to start a DM or create a group.</div>
            ) : combined.map((row) => {
              const Icon = KIND_ICON[row.kind] || MessageCircle;
              const active = threadKey === row.thread_key || (selected && ((row.kind === "dm" && selected.user_id === row.select.user_id) || (row.kind !== "dm" && selected.id === row.select.id)));
              return (
                <div key={row.thread_key} className={`relative group border-b border-slate-100 overflow-hidden ${active ? "bg-white border-l-2 border-l-kb-primary" : row.unread > 0 ? "bg-kb-primary/5 hover:bg-white" : "hover:bg-white"}`}>
                <button
                  onClick={() => { setSelected(row.select); setSearch(""); setSearchResults([]); }}
                  className="w-full text-left px-3 py-2.5 transition-colors flex items-start gap-2.5 overflow-hidden"
                  data-testid={`chat-thread-${row.thread_key}`}
                >
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-kb-primary/10 text-kb-primary text-xs font-medium">
                      {row.kind === "dm" ? (row.title || "?")[0]?.toUpperCase() : <Icon className="w-4 h-4" strokeWidth={1.5} />}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 pr-8 overflow-hidden">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`text-sm text-kb-ink truncate max-w-full ${row.unread > 0 ? "font-bold" : "font-medium"}`}>{row.title}</div>
                      {row.unread > 0 && (
                        <Badge className="bg-kb-primary text-white text-[10px] px-1.5 py-0 h-5">{row.unread}</Badge>
                      )}
                    </div>
                    <div className={`text-[10px] truncate max-w-full mt-0.5 ${row.unread > 0 ? "text-kb-ink font-semibold" : "text-kb-ink-muted kb-mono"}`}>{row.subtitle}</div>
                  </div>
                </button>
                {!row._search && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-2 right-2 opacity-70 hover:opacity-100 p-1 rounded-sm border border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary hover:text-kb-primary"
                        title="Conversation options"
                        data-testid={`chat-thread-${row.thread_key}-menu`}
                      >
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[10rem]">
                      <DropdownMenuItem onClick={() => archiveThread(row.select, !showArchived)}>
                        <Archive className="w-3.5 h-3.5 mr-2" /> {showArchived ? "Restore" : "Archive"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deleteThread(row.select)} className="text-rose-600 focus:text-rose-600">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete conversation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                </div>
              );
            })}
          </ScrollArea>
        </aside>

        {/* RIGHT PANE */}
        <section className="flex-1 min-w-0 flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-kb-ink-muted">
              <div className="text-center">
                <UsersIcon className="w-8 h-8 mx-auto text-kb-ink-light mb-2" strokeWidth={1.5} />
                Pick a conversation to start messaging
              </div>
            </div>
          ) : (
            <>
              <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="w-9 h-9 bg-kb-primary/10">
                    <AvatarFallback className="bg-kb-primary/10 text-kb-primary text-sm font-medium">
                      {(selected.name || "?")[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-kb-ink truncate">{selected.name}</div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                      {selected.kind === "dm" ? "Direct message" : selected.kind === "grp" ? "Custom group" : selected.kind === "team" ? "Team channel" : "Unit channel"}
                    </div>
                  </div>
                </div>
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => initiateCall(true)} data-testid="chat-call-audio">
                          <Phone className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Start voice call</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => initiateCall(false)} data-testid="chat-call-video">
                          <Video className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Start video call</TooltipContent>
                    </Tooltip>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => loadMessages(selected)}>Refresh</DropdownMenuItem>
                        <DropdownMenuItem onClick={async () => { await archiveThread(selected, true); }} data-testid="chat-archive-thread">
                          <Archive className="w-3.5 h-3.5 mr-2" /> Archive conversation
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteThread(selected)} className="text-rose-600 focus:text-rose-600" data-testid="chat-delete-thread">
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete conversation
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelected(null)}>Close conversation</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TooltipProvider>
              </header>
              <ScrollArea className="flex-1 bg-slate-50" data-testid="chat-messages">
                <div className="p-4 space-y-2">
                {messages.length === 0 ? (
                  <div className="text-center text-xs text-kb-ink-muted mt-8">No messages yet — say hi.</div>
                ) : messages.map((m) => {
                  const me = (m.from_user_id || m.user_id) === user?.user_id;
                  if (m.deleted) return (
                    <div key={m.id} className={`flex ${me ? "justify-end" : "justify-start"}`}>
                      <div className="px-3 py-1.5 rounded-2xl text-xs italic text-kb-ink-muted bg-slate-100">Message deleted</div>
                    </div>
                  );
                  // Reactions grouping ({emoji, count, mine})
                  const rxGroups = groupReactions(m.reactions || [], user?.user_id);
                  const parent = m.reply_to_id ? (messages.find((x) => x.id === m.reply_to_id)) : null;
                  return (
                    <div key={m.id} className={`group flex ${me ? "justify-end" : "justify-start"} gap-2`}>
                      {!me && (
                        <Avatar className="w-7 h-7 mt-0.5 shrink-0">
                          <AvatarFallback className="bg-kb-primary/10 text-kb-primary text-[11px]">
                            {(m.from_name || "?")[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`relative max-w-[70%] px-3 py-2 rounded-2xl text-sm shadow-sm ${me ? "bg-kb-primary text-white rounded-br-sm" : "bg-white border border-slate-200 text-kb-ink rounded-bl-sm"}`}>
                        {!me && (m.from_name || m.author_name) && (
                          <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary mb-0.5">{m.from_name || m.author_name}</div>
                        )}
                        {parent && (
                          <div className={`border-l-2 pl-2 mb-1 text-[11px] leading-tight ${me ? "border-white/40 text-white/80" : "border-kb-primary text-kb-ink-muted"}`}>
                            <div className="kb-mono text-[9px] uppercase tracking-wider opacity-80">Reply to {parent.from_name || "message"}</div>
                            <div className="truncate max-w-[240px]">{parent.body?.slice(0, 120) || (parent.attachments?.length ? "📎 attachment" : "…")}</div>
                          </div>
                        )}
                        {m.body && <CallInviteOrBody body={m.body} onDark={me} />}
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {m.attachments.map((a, i) => <AttachmentPreview key={i} att={a} onDark={me} />)}
                          </div>
                        )}
                        <div className={`kb-mono text-[9px] mt-1 ${me ? "text-white/60" : "text-kb-ink-light"}`}>
                          {new Date(m.created_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
                        </div>
                        {rxGroups.length > 0 && (
                          <div className={`flex flex-wrap gap-1 mt-1.5 ${me ? "" : ""}`}>
                            {rxGroups.map((g) => (
                              <button key={g.emoji} onClick={() => toggleReaction(m, g.emoji)}
                                className={`text-[11px] px-1.5 py-0.5 rounded-full border ${g.mine ? "border-kb-primary bg-kb-primary/20" : "border-slate-200 bg-white"} ${me ? "text-kb-ink" : ""}`}>
                                {g.emoji} {g.count}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Minimal hover menu — single 3-dot dropdown */}
                        <div className={`absolute top-1 ${me ? "-left-8" : "-right-8"} opacity-0 group-hover:opacity-100 transition-opacity`}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full bg-white/90 border border-slate-200 shadow-sm hover:bg-white text-kb-ink-muted">
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={me ? "end" : "start"} className="min-w-[8rem]">
                              <div className="flex gap-1 px-2 py-1.5">
                                {["👍", "❤️", "😂", "🎉", "🙏"].map((e) => (
                                  <button key={e} onClick={() => toggleReaction(m, e)} className="text-lg hover:scale-110 transition-transform">{e}</button>
                                ))}
                              </div>
                              <DropdownMenuItem onClick={() => setReplyTo(m)}>
                                <Reply className="w-3.5 h-3.5 mr-2" /> Reply
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { navigator.clipboard?.writeText(m.body || ""); toast.success("Copied"); }}>
                                Copy text
                              </DropdownMenuItem>
                              {me && (
                                <DropdownMenuItem onClick={() => deleteMessage(m)} className="text-rose-600 focus:text-rose-600">
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
                </div>
              </ScrollArea>

              {/* Reply chip */}
              {replyTo && (
                <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 flex items-center gap-2" data-testid="chat-reply-chip">
                  <div className="w-0.5 h-6 bg-kb-primary rounded-full" />
                  <div className="min-w-0 flex-1">
                    <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary">Replying to {replyTo.from_name || "message"}</div>
                    <div className="text-[11px] text-kb-ink-muted truncate">{replyTo.body?.slice(0, 140) || "📎 attachment"}</div>
                  </div>
                  <button onClick={() => setReplyTo(null)} className="p-1 text-kb-ink-muted hover:text-rose-600" title="Cancel reply"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {/* Attachment tray */}
              {attachments.length > 0 && (
                <div className="px-3 py-2 border-t border-slate-200 bg-white flex flex-wrap gap-1.5" data-testid="chat-attachments-tray">
                  {attachments.map((a, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 border border-slate-200 rounded-sm px-2 py-1 text-xs">
                      <FileText className="w-3 h-3 text-kb-ink-muted" />
                      <span className="truncate max-w-[160px]">{a.name}</span>
                      <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-kb-ink-muted hover:text-rose-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <form
                onSubmit={send}
                className="p-3 border-t border-slate-200 flex flex-col gap-2 bg-white"
              >
                {selected.kind !== "dm" && (
                  <label className="flex items-center gap-2 text-[11px] text-kb-ink-muted select-none">
                    <input type="checkbox" checked={sendAsIndividual} onChange={(e) => setSendAsIndividual(e.target.checked)}
                      className="accent-kb-primary" data-testid="chat-send-individual-toggle" />
                    <Radio className="w-3 h-3" /> Send as individual DM to each member (Broadcast-list style)
                  </label>
                )}
                <div className="flex items-end gap-2">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()}>
                        <Paperclip className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Attach a file</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <input type="file" ref={fileRef} onChange={onAttach} className="hidden" />
                <Textarea
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    // Auto-expand up to max-h via inline style.
                    const el = e.target;
                    el.style.height = "auto";
                    el.style.height = Math.min(el.scrollHeight, 240) + "px";
                  }}
                  onKeyDown={(e) => {
                    // Enter → send; Ctrl/Shift+Enter → newline.
                    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Write a message… (Ctrl/Shift+Enter for newline)"
                  rows={1}
                  className="text-sm flex-1 min-h-[38px] max-h-[240px] resize-none whitespace-pre-wrap break-words"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={busy || (!draft.trim() && attachments.length === 0)}
                  className="bg-kb-primary text-white hover:bg-kb-primary/90"
                  data-testid="chat-send"
                >
                  <Send className="w-3.5 h-3.5" /> Send
                </Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>

      {showGroupModal && <NewGroupModal onClose={() => setShowGroupModal(false)} onCreated={() => { setShowGroupModal(false); loadGroupThreads(); }} />}
      {showNewChat && <NewChatPicker onClose={() => setShowNewChat(false)} onPicked={(sel) => { setShowNewChat(false); setSelected(sel); loadGroupThreads(); }} me={user} />}
      {showBroadcast && <BroadcastRolesModal onClose={() => setShowBroadcast(false)} onSent={() => { setShowBroadcast(false); loadDmInbox(); loadGroupThreads(); }} me={user} />}
    </div>
  );
}

// ---- Reactions helper ------------------------------------------------------
function groupReactions(reactions, myUserId) {
  const map = new Map();
  for (const r of reactions) {
    const e = r.emoji;
    if (!map.has(e)) map.set(e, { emoji: e, count: 0, mine: false });
    const g = map.get(e);
    g.count += 1;
    if (r.user_id === myUserId) g.mine = true;
  }
  return Array.from(map.values());
}

// ---- New chat picker (Team / Org unit / Custom group / DM) -----------------
function NewChatPicker({ onClose, onPicked, me }) {
  const [tab, setTab] = useState("dm"); // dm | team | unit | grp
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [units, setUnits] = useState([]);
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    if (tab === "team") return; // no fetch needed
    if (tab === "unit" && units.length === 0) api.get("/org/units").then((r) => setUnits(r.data || [])).catch(() => {});
    if (tab === "grp" && groups.length === 0) api.get("/chat/groups").then((r) => setGroups(r.data || [])).catch(() => {});
  }, [tab]); // eslint-disable-line
  useEffect(() => {
    const query = q.trim();
    if (tab !== "dm" || query.length < 2) { setUsers([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/workspace/dm/search-users`, { params: { q: query } }); setUsers(r.data || []); }
      catch { setUsers([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, tab]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="new-chat-modal">
        <DialogHeader>
          <DialogTitle className="font-serif">Start a new conversation</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-slate-200 -mx-6 px-6">
          {[
            { k: "dm", label: "User" },
            { k: "team", label: "My team" },
            { k: "unit", label: "Org unit" },
            { k: "grp", label: "Group" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-2 kb-mono text-[10px] uppercase tracking-wider transition-colors ${tab === t.k ? "text-kb-primary border-b-2 border-kb-primary" : "text-kb-ink-muted hover:text-kb-ink"}`}
              data-testid={`new-chat-tab-${t.k}`}>{t.label}</button>
          ))}
        </div>
        <div className="min-h-[240px] max-h-[360px] overflow-y-auto">
          {tab === "dm" && (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a person…" className="kb-input text-sm w-full" autoFocus />
              <div className="mt-2 space-y-0">
                {users.map((u) => (
                  <button key={u.user_id} onClick={() => onPicked({ kind: "dm", user_id: u.user_id, name: u.name || u.email })}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-2">
                    <Avatar className="w-8 h-8"><AvatarFallback className="bg-kb-primary/10 text-kb-primary text-xs">{(u.name || u.email || "?")[0]?.toUpperCase()}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <div className="text-sm text-kb-ink truncate">{u.name || u.email}</div>
                      <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{u.email} · {u.role}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
          {tab === "team" && (
            <div>
              {me?.team_section ? (
                <button onClick={() => onPicked({ kind: "team", id: me.team_section, name: `# ${me.team_section}` })}
                  className="w-full text-left p-3 border border-slate-200 rounded-sm hover:border-kb-primary transition-colors">
                  <div className="text-sm text-kb-ink flex items-center gap-2"><Hash className="w-4 h-4 text-kb-primary" /> {me.team_section}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted mt-1">Chat with everyone in your team_section</div>
                </button>
              ) : (
                <div className="text-sm text-kb-ink-muted text-center py-8">You don&apos;t belong to a team yet. Ask an admin to assign one.</div>
              )}
            </div>
          )}
          {tab === "unit" && (
            <div className="space-y-1">
              {units.length === 0 && <div className="text-sm text-kb-ink-muted text-center py-8">You&apos;re not a member of any org unit.</div>}
              {units.map((u) => (
                <button key={u.id} onClick={() => onPicked({ kind: "unit", id: u.id, name: `⊞ ${u.name}` })}
                  className="w-full text-left p-2.5 border border-slate-200 rounded-sm hover:border-kb-primary transition-colors">
                  <div className="text-sm text-kb-ink">{u.name}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted">{(u.members || []).length} members</div>
                </button>
              ))}
            </div>
          )}
          {tab === "grp" && (
            <div className="space-y-1">
              {groups.length === 0 && <div className="text-sm text-kb-ink-muted text-center py-8">No custom groups yet. Use &ldquo;New group&rdquo; to create one.</div>}
              {groups.map((g) => (
                <button key={g.id} onClick={() => onPicked({ kind: "grp", id: g.id, name: g.name })}
                  className="w-full text-left p-2.5 border border-slate-200 rounded-sm hover:border-kb-primary transition-colors">
                  <div className="text-sm text-kb-ink">{g.name}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted">{(g.member_user_ids || []).length} members</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Attachment preview (image / PDF card / other) --------------------------
function AttachmentPreview({ att, onDark }) {
  const mime = att.mimeType || "";
  const isImage = mime.startsWith("image/");
  const isPdf = mime === "application/pdf" || (att.name || "").toLowerCase().endsWith(".pdf");
  // Relative URLs coming from /api/chat/upload get prefixed with the
  // current backend so preview + prod each serve their own files.
  const href = att.url?.startsWith("/") ? `${BACKEND_URL || ""}${att.url}` : att.url;
  const border = onDark ? "border-white/30" : "border-slate-200";
  const bg = onDark ? "bg-white/10" : "bg-slate-50";
  const iconCls = onDark ? "text-white/80" : "text-kb-primary";
  if (isImage) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`inline-block border ${border} rounded-sm overflow-hidden`}>
        <img src={href} alt={att.name} className="max-h-40 max-w-[240px] object-cover" />
      </a>
    );
  }
  if (isPdf) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`flex items-center gap-2 border ${border} ${bg} rounded-sm px-2.5 py-2 hover:opacity-90 transition-opacity min-w-[200px] max-w-[280px]`}>
        <div className={`w-9 h-11 flex items-center justify-center rounded-sm bg-rose-500/90 text-white text-[9px] font-bold shrink-0`}>PDF</div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{att.name}</div>
          <div className="kb-mono text-[9px] uppercase tracking-wider opacity-70">
            {formatBytes(att.size)} · Click to preview
          </div>
        </div>
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 border ${border} ${bg} rounded-sm px-2 py-1 text-xs hover:underline`}>
      <FileText className={`w-3 h-3 ${iconCls}`} /> {att.name}
    </a>
  );
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---- Call invite rendering -------------------------------------------------
// Messages produced by initiateCall() look like:
//   "📞 Voice call started — join: https://…"
// We hide the raw URL and render a "Join call" button instead so members can
// click straight into the persistent inline call panel.
function CallInviteOrBody({ body, onDark }) {
  const callRe = /^(📞|📹)\s*(Voice|Video) call started — join:\s*(https?:\/\/\S+)/i;
  const m = body.match(callRe);
  if (m) {
    const [, icon, kind, url] = m;
    return (
      <button
        type="button"
        onClick={() => {
          try {
            const evt = new CustomEvent("khojbyte:join-call", { detail: { url, audioOnly: kind.toLowerCase() === "voice" } });
            window.dispatchEvent(evt);
          } catch { /* fallback: open in new tab */ window.open(url, "_blank", "noopener,noreferrer"); }
        }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${onDark ? "bg-white/15 border-white/30 hover:bg-white/25 text-white" : "bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-800"}`}
        data-testid="chat-call-invite-join"
      >
        {icon} {kind} call · Join
      </button>
    );
  }
  // Detect plain URLs and render each as a clickable chip; keep every other
  // character verbatim so newlines + whitespace survive (critical for
  // multi-line messages). Mirror WhatsApp / Slack behaviour.
  const urlRe = /(https?:\/\/[^\s<>()\[\]{}"']+)/gi;
  const parts = String(body || "").split(urlRe);
  return (
    <div className="whitespace-pre-wrap break-words">
      {parts.map((part, i) => {
        if (!part) return null;
        if (urlRe.test(part)) {
          urlRe.lastIndex = 0; // reset regex state (global flag)
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noreferrer noopener"
              className={`inline break-all underline underline-offset-2 ${onDark ? "text-white" : "text-kb-primary hover:text-kb-primary/80"}`}
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </div>
  );
}

// ---- New group modal -------------------------------------------------------
function NewGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/workspace/dm/search-users`, { params: { q: query } }); setResults(r.data || []); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const create = async () => {
    if (!name.trim()) { toast.error("Group name required"); return; }
    setBusy(true);
    try {
      await api.post("/chat/groups", { name: name.trim(), member_user_ids: picked.map((p) => p.user_id) });
      toast.success("Group created");
      onCreated?.();
    } catch { toast.error("Couldn't create group"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="new-group-modal">
        <DialogHeader>
          <DialogTitle className="font-serif">New custom group</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="kb-input text-sm mt-1 w-full" data-testid="new-group-name" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Members</label>
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {picked.map((p) => (
                  <Badge key={p.user_id} variant="secondary" className="gap-1">
                    {p.name || p.email}
                    <button onClick={() => setPicked((arr) => arr.filter((x) => x.user_id !== p.user_id))} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="kb-input text-sm mt-2 w-full" data-testid="new-group-search" />
            {results.length > 0 && (
              <ScrollArea className="mt-1 border border-slate-200 rounded-sm max-h-40">
                {results.map((u) => (
                  <button key={u.user_id}
                    onClick={() => { setPicked((arr) => arr.some((x) => x.user_id === u.user_id) ? arr : [...arr, u]); setQ(""); setResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                    <div className="text-kb-ink">{u.name || u.email}</div>
                    <div className="kb-mono text-[10px] text-kb-ink-muted">{u.email} · {u.role}</div>
                  </button>
                ))}
              </ScrollArea>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy} className="bg-kb-primary text-white hover:bg-kb-primary/90" data-testid="new-group-create">
            {busy ? "Creating…" : "Create group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---- Broadcast by role modal ----------------------------------------------
// Lets a caller send one message to every user matching a set of roles.
// Two delivery modes:
//   • Individual DMs  → recipients don't see each other (WhatsApp broadcast)
//   • Group thread    → members can reply and see each other
const ROLE_OPTIONS = [
  { k: "intern", label: "Intern" },
  { k: "employee", label: "Employee" },
  { k: "mentor", label: "Mentor" },
  { k: "tutor", label: "Tutor" },
  { k: "client", label: "Client" },
  { k: "partner_institution", label: "Partner" },
  { k: "admin", label: "Admin" },
];

function BroadcastRolesModal({ onClose, onSent, me }) {
  const [roles, setRoles] = React.useState(new Set());
  const [body, setBody] = React.useState("");
  const [asIndividual, setAsIndividual] = React.useState(true);
  const [attachments, setAttachments] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  const isPrivileged = ["admin", "super_admin"].includes(me?.role);
  const canBroadcast = new Set(
    isPrivileged
      ? ROLE_OPTIONS.map((r) => r.k)
      : ["employee", "mentor", "tutor"].includes(me?.role)
        // Staff (employee / mentor / tutor) can broadcast to peers + interns.
        ? ["intern", "employee", "mentor", "tutor"]
        : ["intern"]
  );
  const toggle = (k) => setRoles((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Max 12MB"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/chat/upload", fd);
      setAttachments((a) => [...a, { name: r.data.name, url: r.data.url, size: r.data.size, mimeType: r.data.mimeType }]);
    } catch { toast.error("Upload failed"); }
    finally { e.target.value = ""; }
  };

  const send = async () => {
    const list = Array.from(roles);
    if (list.length === 0) { toast.error("Pick at least one role"); return; }
    if (!body.trim() && attachments.length === 0) { toast.error("Message body empty"); return; }
    setBusy(true);
    try {
      const r = await api.post("/chat/broadcast-roles", { roles: list, body: body.trim(), attachments, as_individual: asIndividual });
      toast.success(`Broadcast sent — ${r.data?.sent || 0} recipient(s)`);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="broadcast-roles-modal">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2"><Radio className="w-4 h-4 text-kb-primary" /> Broadcast to roles</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Recipient roles</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ROLE_OPTIONS.filter((r) => canBroadcast.has(r.k)).map((r) => {
                const on = roles.has(r.k);
                return (
                  <button key={r.k} type="button" onClick={() => toggle(r.k)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[11px] kb-mono uppercase tracking-wider transition-colors ${on ? "bg-kb-primary text-white border-kb-primary" : "border-slate-200 text-kb-ink-muted hover:border-kb-ink"}`}
                    data-testid={`broadcast-role-${r.k}`}
                  >
                    <input type="checkbox" checked={on} onChange={() => {}} className="pointer-events-none accent-white" /> {r.label}
                  </button>
                );
              })}
            </div>
            {!isPrivileged && <div className="kb-mono text-[9px] text-kb-ink-light mt-1">You can broadcast to interns and staff peers (admin approval needed for clients / partners).</div>}
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Message</label>
            <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your announcement…"
              className="mt-1 text-sm" data-testid="broadcast-body" />
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Paperclip className="w-3.5 h-3.5" /> Attach
            </Button>
            <input type="file" ref={fileRef} onChange={uploadFile} className="hidden" />
            {attachments.length > 0 && <span className="kb-mono text-[10px] text-emerald-700">{attachments.length} file(s) attached</span>}
          </div>
          <label className="flex items-start gap-2 text-[12px] text-kb-ink select-none border border-slate-200 rounded-sm p-2.5">
            <input type="checkbox" checked={asIndividual} onChange={(e) => setAsIndividual(e.target.checked)}
              className="mt-0.5 accent-kb-primary" data-testid="broadcast-as-individual" />
            <span>
              <span className="font-medium">Send as individual DM to each recipient</span>
              <span className="block kb-mono text-[10px] text-kb-ink-muted mt-0.5">
                {asIndividual
                  ? "Recipients get a private thread with you and can't see each other."
                  : "Everyone joins one shared group thread and can reply to the room."}
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || roles.size === 0} className="bg-kb-primary text-white hover:bg-kb-primary/90" data-testid="broadcast-send">
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Broadcast"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
